import React, {
    useCallback,
    useMemo, useState,
} from 'react'
import useTranslate from 'helpers/hooks/useTranslate'
import {
    ConstrainMode, DefaultButton, DetailsListLayoutMode, IconButton, SelectionMode, ShimmeredDetailsList,
} from '@fluentui/react'
import Card from 'components/containers/card'
import VacationWeek from 'requests/objects/vacationWeek'
import VacationWeekModal from 'components/pages/vacationWeekModal'
import styles from 'styles/pages/logistic-constraints/pivot-logistic-constraints.module.scss'
import CompaniesHandler from 'requests/handlers/companiesHandler'

/**
 * PivotVacationWeeks
 * @param {object} props Props
 * @param {string} props.lang Lang
 * @param {CompaniesHandler} props.companiesHandler companiesHandler
 * @param {VacationWeek[]} props.vacationWeeks VacationWeeks
 * @param {number} props.companyId companyId
 * @param {(vacationWeek: VacationWeek, isDeletion: boolean) => void} props.onChange onChange
 * @returns {JSX.Element} Element
 */
export default function PivotVacationWeeks({
    lang, vacationWeeks, companiesHandler, companyId, onChange,
}) {
    const { tKey } = useTranslate({ lang })
    const [selectedVCId, setSelectedVCId] = useState(null)
    const [isVisible, setIsVisible] = useState(false)
    const [isDeletion, setIsDeletion] = useState(false)

    const updateVCList = useCallback(
        /**
         * @param {VacationWeek} vacationWeek vacationWeek
         */
        vacationWeek => {
            if (vacationWeek)
                onChange(vacationWeek, isDeletion)
            if (isDeletion)
                setIsDeletion(false)
            setIsVisible(false)
        }, [onChange, isDeletion],
    )

    const columns = useMemo(() => /** @type {import('@fluentui/react').IColumn[]} */([
        {
            key: 'year',
            name: tKey('year'),
            fieldName: 'year',
            minWidth: 300,
            maxWidth: 400,
            isResizable: true,
        },
        {
            key: 'week',
            name: tKey('week'),
            fieldName: 'week',
            minWidth: 300,
            maxWidth: 400,
            isResizable: true,
        },
        {
            key: 'actions',
            name: '',
            minWidth: 96,
            maxWidth: 96,
            isResizable: false,
            className: styles['list-item'],
            onRender:
                // eslint-disable-next-line react/no-unstable-nested-components
                vacationWeek => (
                    <div className={styles['actions-buttons']}>
                        <IconButton
                            iconProps={{ iconName: 'Edit' }}
                            title={tKey('edit')}
                            onClick={e => {
                                e.preventDefault()
                                setSelectedVCId(vacationWeek.vacationWeekId)
                                setIsVisible(true)
                            }}
                        />
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            onClick={e => {
                                e.preventDefault()
                                setSelectedVCId(vacationWeek.vacationWeekId)
                                setIsDeletion(true)
                                setIsVisible(true)
                            }}
                        />
                    </div>
                ),
        },
    ]), [tKey])

    return (
        <>
            <Card
                title={tKey('vacationWeeks')}
                iconName="PageData"
                headerComponent={(
                    <DefaultButton
                        onClick={() => {
                            setSelectedVCId(null)
                            setIsVisible(true)
                        }}
                        text={tKey('add')}
                    />
                )}
            >
                <ShimmeredDetailsList
                    items={vacationWeeks}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    onShouldVirtualize={() => true}
                    layoutMode={DetailsListLayoutMode.justified}
                    constrainMode={ConstrainMode.unconstrained}
                    onRenderDetailsHeader={(props, defaultRender) => defaultRender({ ...props, styles: { root: { paddingTop: 0 } } })}
                />
            </Card>
            <VacationWeekModal
                handler={companiesHandler}
                isVisible={isVisible}
                lang={lang}
                onChange={updateVCList}
                isReadOnly={false}
                vacationWeekdata={vacationWeeks.find(x => x.vacationWeekId === selectedVCId)}
                companyId={companyId}
                isDeletion={isDeletion}
            />
        </>
    )
}
